import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Represents a snapshot of the website builder state
 */
export interface StateSnapshot {
  // Core JSON data
  jsonData: any;
  
  // UI state
  websiteElements?: any[];
  heroPanelData?: any;
  infoPanelData?: any;
  testimonialsData?: any;
  bookingData?: any;
  contactData?: any;
  headerData?: any;
  footerData?: any;
  brandingData?: any;
  
  // Metadata
  timestamp: number;
  description?: string;
  
  // Page context
  currentPageType?: string;
  currentInfoSectionKey?: string;
}

/**
 * Configuration for undo/redo behavior
 */
export interface UndoRedoConfig {
  maxHistorySize: number;
  debounceMs: number;
  enableCompression: boolean;
}

/**
 * Service for managing undo/redo functionality in the website builder
 * 
 * Features:
 * - State snapshot management with deep cloning
 * - Debounced state capture to group rapid changes
 * - Memory-efficient history with configurable size limits
 * - Observable streams for reactive UI updates
 */
@Injectable({
  providedIn: 'root'
})
export class UndoRedoService {
  private undoStack: StateSnapshot[] = [];
  private redoStack: StateSnapshot[] = [];
  private masterState: StateSnapshot | null = null;
  private currentState: StateSnapshot | null = null;
  
  // Debouncing
  private debounceTimer: any = null;
  private pendingSnapshot: StateSnapshot | null = null;
  
  // Configuration
  private config: UndoRedoConfig = {
    maxHistorySize: 50,
    debounceMs: 1000,
    enableCompression: true
  };
  
  // Observables for UI state
  private canUndoSubject = new BehaviorSubject<boolean>(false);
  private canRedoSubject = new BehaviorSubject<boolean>(false);
  private stateChangeSubject = new BehaviorSubject<StateSnapshot | null>(null);
  
  public canUndo$: Observable<boolean> = this.canUndoSubject.asObservable();
  public canRedo$: Observable<boolean> = this.canRedoSubject.asObservable();
  public stateChange$: Observable<StateSnapshot | null> = this.stateChangeSubject.asObservable();
  
  constructor(private logger: LoggerService) {}
  
  /**
   * Initialize the service with master state (initial state from API)
   */
  setMasterState(state: StateSnapshot): void {
    this.masterState = this.deepClone(state);
    this.currentState = this.deepClone(state);
    this.undoStack = [];
    this.redoStack = [];
    this.updateCanUndoRedo();
    this.logger.debug('🎯 Undo/Redo: Master state set', state);
  }
  
  /**
   * Record a new state change (debounced to group rapid changes)
   */
  recordState(state: StateSnapshot, immediate = false): void {
    if (!this.masterState) {
      this.logger.warn('⚠️ Undo/Redo: Master state not set, initializing with current state');
      this.setMasterState(state);
      return;
    }
    
    this.pendingSnapshot = this.deepClone(state);
    
    // 🔥 UPDATE: Enable undo button immediately when change is pending
    // This gives instant feedback to the user while API sync happens in background
    this.updateCanUndoRedo();
    
    // Clear any existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    if (immediate) {
      this.commitPendingSnapshot();
    } else {
      // Debounce to group rapid changes
      this.debounceTimer = setTimeout(() => {
        this.commitPendingSnapshot();
      }, this.config.debounceMs);
    }
  }
  
  /**
   * Commit the pending snapshot to the undo stack
   */
  private commitPendingSnapshot(): void {
    if (!this.pendingSnapshot) return;
    
    // Don't record if state hasn't actually changed
    if (this.currentState && this.areStatesEqual(this.currentState, this.pendingSnapshot)) {
      this.logger.debug('⏭️ Undo/Redo: State unchanged, skipping record');
      this.pendingSnapshot = null;
      return;
    }
    
    // Add current state to undo stack
    if (this.currentState) {
      this.undoStack.push(this.deepClone(this.currentState));
      
      // Limit stack size
      if (this.undoStack.length > this.config.maxHistorySize) {
        this.undoStack.shift();
      }
    }
    
    // Clear redo stack (new change invalidates redo history)
    this.redoStack = [];
    
    // Update current state
    this.currentState = this.deepClone(this.pendingSnapshot);
    this.pendingSnapshot = null;
    
    this.updateCanUndoRedo();
    this.logger.debug('✅ Undo/Redo: State recorded', {
      undoStackSize: this.undoStack.length,
      redoStackSize: this.redoStack.length
    });
  }
  
  /**
   * Undo the last change
   */
  undo(): StateSnapshot | null {
    if (!this.canUndo()) {
      this.logger.warn('⚠️ Undo/Redo: Cannot undo - no history');
      return null;
    }
    
    // Commit any pending snapshot first
    if (this.pendingSnapshot) {
      this.commitPendingSnapshot();
    }
    
    // Move current state to redo stack
    if (this.currentState) {
      this.redoStack.push(this.deepClone(this.currentState));
    }
    
    // Pop from undo stack
    const previousState = this.undoStack.pop();
    if (!previousState) return null;
    
    this.currentState = this.deepClone(previousState);
    this.updateCanUndoRedo();
    this.stateChangeSubject.next(this.currentState);
    
    this.logger.debug('↩️ Undo executed', {
      undoStackSize: this.undoStack.length,
      redoStackSize: this.redoStack.length
    });
    
    return this.deepClone(this.currentState);
  }
  
  /**
   * Redo the last undone change
   */
  redo(): StateSnapshot | null {
    if (!this.canRedo()) {
      this.logger.warn('⚠️ Undo/Redo: Cannot redo - no redo history');
      return null;
    }
    
    // Move current state to undo stack
    if (this.currentState) {
      this.undoStack.push(this.deepClone(this.currentState));
    }
    
    // Pop from redo stack
    const nextState = this.redoStack.pop();
    if (!nextState) return null;
    
    this.currentState = this.deepClone(nextState);
    this.updateCanUndoRedo();
    this.stateChangeSubject.next(this.currentState);
    
    this.logger.debug('↪️ Redo executed', {
      undoStackSize: this.undoStack.length,
      redoStackSize: this.redoStack.length
    });
    
    return this.deepClone(this.currentState);
  }
  
  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoStack.length > 0 || (this.pendingSnapshot !== null && this.currentState !== null);
  }
  
  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }
  
  /**
   * Get the current state
   */
  getCurrentState(): StateSnapshot | null {
    return this.currentState ? this.deepClone(this.currentState) : null;
  }
  
  /**
   * Get the master state
   */
  getMasterState(): StateSnapshot | null {
    return this.masterState ? this.deepClone(this.masterState) : null;
  }
  
  /**
   * Reset to master state
   */
  resetToMaster(): StateSnapshot | null {
    if (!this.masterState) return null;
    
    this.undoStack = [];
    this.redoStack = [];
    this.currentState = this.deepClone(this.masterState);
    this.updateCanUndoRedo();
    this.stateChangeSubject.next(this.currentState);
    
    this.logger.debug('🔄 Reset to master state');
    return this.deepClone(this.currentState);
  }
  
  /**
   * Clear all history
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.masterState = null;
    this.currentState = null;
    this.pendingSnapshot = null;
    
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    this.updateCanUndoRedo();
    this.logger.debug('🗑️ Undo/Redo: History cleared');
  }
  
  /**
   * Update configuration
   */
  updateConfig(config: Partial<UndoRedoConfig>): void {
    this.config = { ...this.config, ...config };
  }
  
  /**
   * Get history info for debugging
   */
  getHistoryInfo(): { undoCount: number; redoCount: number; hasMaster: boolean } {
    return {
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
      hasMaster: this.masterState !== null
    };
  }
  
  /**
   * Deep clone an object using structured cloning
   */
  private deepClone(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    
    try {
      // Use structuredClone if available (modern browsers)
      if (typeof structuredClone === 'function') {
        return structuredClone(obj);
      }
      
      // Fallback to JSON method (works for most cases)
      return JSON.parse(JSON.stringify(obj));
    } catch (error) {
      this.logger.error('❌ Deep clone failed:', error);
      return obj;
    }
  }
  
  /**
   * Compare two states for equality
   */
  private areStatesEqual(state1: StateSnapshot, state2: StateSnapshot): boolean {
    try {
      // Compare JSON data (main state)
      const json1 = JSON.stringify(state1.jsonData);
      const json2 = JSON.stringify(state2.jsonData);
      return json1 === json2;
    } catch (error) {
      this.logger.error('❌ State comparison failed:', error);
      return false;
    }
  }
  
  /**
   * Update can undo/redo observables
   */
  private updateCanUndoRedo(): void {
    this.canUndoSubject.next(this.canUndo());
    this.canRedoSubject.next(this.canRedo());
  }
  
  /**
   * Force commit any pending snapshot (useful before navigation or save)
   */
  flushPendingSnapshot(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.commitPendingSnapshot();
  }
}

